// packages/ui/src/hooks/useSessionPersistence.ts
import { useEffect, useRef } from 'react';
import type { PlatformBridge } from '@slate/core';
import { useDocStore } from '../store/docStore';
import { useToolsStore } from '../tools/toolsStore';
import { writeManifest } from '../store/sessionManifest';
import { registerFlusher } from '../session/closeController';

const MANIFEST_DEBOUNCE_MS = 400;

/**
 * Writes the session manifest (tabs, roots, active, tools snapshot) to the
 * native session store, debounced on any structural change and flushed
 * immediately at close/quit.
 */
export function useSessionPersistence(platform: PlatformBridge): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useRef<() => Promise<void>>(async () => {});
  flush.current = () => writeManifest(platform);

  useEffect(() => registerFlusher(() => flush.current()), []);

  useEffect(() => {
    if (!platform.session) return;
    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void writeManifest(platform), MANIFEST_DEBOUNCE_MS);
    };
    let lastDocs = useDocStore.getState().docs;
    let lastActive = useDocStore.getState().activeId;
    let lastRoots = useDocStore.getState().workspaceRoots;
    const unsubDoc = useDocStore.subscribe((s) => {
      if (s.docs !== lastDocs || s.activeId !== lastActive || s.workspaceRoots !== lastRoots) {
        lastDocs = s.docs;
        lastActive = s.activeId;
        lastRoots = s.workspaceRoots;
        schedule();
      }
    });
    let lastTools = useToolsStore.getState();
    const unsubTools = useToolsStore.subscribe((t) => {
      if (
        t.instances !== lastTools.instances ||
        t.activeInstanceId !== lastTools.activeInstanceId ||
        t.panel !== lastTools.panel
      ) {
        lastTools = t;
        schedule();
      }
    });
    return () => {
      unsubDoc();
      unsubTools();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [platform]);
}
